import { Input, InputProps } from 'modules/ui/atoms/Input'
import { KeyboardEvent, KeyboardEventHandler, useEffect, useState } from 'react'
import _ from 'lodash'
import { useTranslation } from 'next-i18next'

/**
 * ExpirationDateInput is a specialized text input meant for credit card expiration dates
 * This is specifically used for non-Stripe CC processors
 * The UX is based on the ux for the Stripe expiration date input
 */
const ExpirationDateInput = ({
  onChange,
  onBlur,
  label,
  value,
  error,
  required,
  inputRef,
}: InputProps) => {
  const { t } = useTranslation()
  const SLASH_WITH_SPACES = t('ExpirationDateInput.separator', ' / ')
  const [expDate, setExpDate] = useState(value ?? '')

  useEffect(() => {
    onChange(expDate)
  }, [expDate])

  /**
   * handleOnChange is a "smart" change handler
   * It ensures the user types a date following a MM / YY format
   * It also ensures that MM is a valid month by adjusting value based on assumed user intention
   * @param change
   * @returns
   */
  const handleOnChange = (change: string) => {
    // If the current value is length 5 (MM / ) and then change has decreased to length 4 (MM /)
    // Delete up to first character - we assume the user wants to change the second digit of month
    if (expDate.length === 5 && change.length === 4) {
      setExpDate(expDate[0])
      return
    }

    const isFirstDigit = change.length === 1

    // If the first character typed is a digit between 2 and 9,
    // Reformat the number to 02 - 09 and add slash with spaces
    if (isFirstDigit && Number(change) >= 2 && Number(change) <= 9) {
      const formattedNumber = Number(change).toLocaleString(undefined, { minimumIntegerDigits: 2 })
      setExpDate(`${formattedNumber}${SLASH_WITH_SPACES}`)
      return
    }

    const isSecondDigit = change.length === 2

    // If the second character typed is - or /
    // Reformat first digit to MM and add slash with spaces
    // "1/" -> "01 / "
    if (isSecondDigit) {
      if (_.endsWith(change, '-') || _.endsWith(change, '/')) {
        const formattedNumber = Number(change[0]).toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })

        setExpDate(`${formattedNumber}${SLASH_WITH_SPACES}`)
        return
      }

      // If the second character typed is a digit or space
      // And the two characters together are a valid month, add slash with spaces
      // 11 -> "11 / "
      // "1 " -> "01 / "
      // If its not a valid month (ex: 13)
      // Reformat so first digit refers to month and second digit is first digit of year
      // 13 -> "01 / 3"
      if (Number(change) <= 12) {
        const formattedNumber = Number(change).toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })

        setExpDate(`${formattedNumber.trim()}${SLASH_WITH_SPACES}`)
      } else {
        setExpDate(`0${change[0]}${SLASH_WITH_SPACES}${change[1]}`)
      }

      return
    }

    //For all other changes, don't reformat
    setExpDate(change)
  }

  /**
   * handleOnKeyDown fires before handleOnChange
   * Here, we want to prevent any invalid keys from hitting handleOnChange
   * Operational keys, such as arrows, backspace, etc are considered valid
   * @param e
   * @returns
   */
  const handleOnKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!isValidKey(e)) {
      e.preventDefault()
      return
    }

    // if value is 0, prevent anything but 1-9

    if (expDate === '0' && !isNaN(Number(e.key))) {
      if (Number(e.key) < 1 || Number(e.key) > 9) {
        e.preventDefault()
      }
    }
  }

  const isValidKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key
    const ctrlKey = e.ctrlKey || e.metaKey

    const isNumber = Number.isInteger(Number(key))
    const isBackspaceOrDelete = key === 'Backspace' || key === 'Delete'
    const isArrowKey = key === 'ArrowLeft' || key === 'ArrowRight'
    const isPasteOrCopy = (ctrlKey && key === 'v') || (ctrlKey && key === 'c')
    const isTab = key === 'Tab'
    const isEnter = key === 'Enter'

    const isSlash = key === '/' && !_.includes(expDate, '/')
    const isDash = key === '-' && !_.includes(expDate, '/')

    return (
      isNumber ||
      isBackspaceOrDelete ||
      isArrowKey ||
      isPasteOrCopy ||
      isTab ||
      isEnter ||
      isSlash ||
      isDash
    )
  }

  return (
    <Input
      value={expDate}
      onChange={handleOnChange}
      onKeyDown={handleOnKeyDown}
      onBlur={onBlur}
      label={label}
      error={error}
      type="text"
      required={required}
      inputRef={inputRef}
      placeholder={t('ExpirationDateInput.placeholder', 'MM / YY')}
      maxLength={7}
      hasOverflow={false}
      hasCounter={false}
      overwriteValueWithFocus
    />
  )
}

export default ExpirationDateInput
